import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslations } from 'use-intl';
import type { SetRangeFormFields } from '../types';
import { parseEther } from 'viem';
import { MAX_TICK, MIN_TICK, feeTierToTickSpacing } from 'nftx.js';

type Form = UseFormReturn<SetRangeFormFields>;

export interface Props {
  symbol: string;
  feeTier: string;
  poolType: 'pair' | 'eth' | 'nft';
  control: Form['control'];
  setValue: Form['setValue'];
  canEditRange: boolean;
  setDisabled: (v: boolean) => void;
  onTickLowerChange: (v: number) => void;
  onTickUpperChange: (v: number) => void;
  onMinPriceChange: (v: string) => void;
  onMaxPriceChange: (v: string) => void;
  showTicks: boolean;
  name: 'minPriceInEth' | 'maxPriceInEth';
  tickKey: 'tickLower' | 'tickUpper';
}

export const PriceInEth = ({
  symbol,
  feeTier,
  control,
  setValue,
  poolType,
  canEditRange,
  setDisabled,
  onTickLowerChange,
  onTickUpperChange,
  onMinPriceChange,
  onMaxPriceChange,
  showTicks,
  name,
  tickKey,
}: Props) => {
  // formatting
  const t = useTranslations('CreateLiquidityPositionForms.SetRange');
  const tickSpacing = feeTierToTickSpacing(Number(feeTier));

  // watch for when the actual form tick values are updated - source of truth
  const {
    tickLower = MIN_TICK,
    tickUpper = MAX_TICK,
    price = '0',
    fullRange = false,
  } = useWatch({
    control,
  });

  const hasPrice = !!price && !!Number(price) && !Number.isNaN(Number(price));

  return (
    <div className="border rounded-md border-gray-300 px-4 py-2 bg-gray-50 text-center space-y-1 focus-within:border-pink-500">
      <label className="block text-sm font-medium">{t(`range.${name}`)}</label>
      {fullRange ? (
        <span className="p-1 text-center text-lg border-none bg-gray-50 min-w-0 w-full mx-2">
          {name === 'minPriceInEth' ? '0' : '∞'}
        </span>
      ) : (
        <Controller
          name={name}
          control={control}
          rules={{
            validate(v, values) {
              const value = parseEther(v || '0');
              const current = parseEther(values.price || '0');
              const minPriceInEth = parseEther(values.minPriceInEth || '0');
              const maxPriceInEth = parseEther(values.maxPriceInEth || '0');

              if (minPriceInEth > maxPriceInEth) {
                return 'Upper price must exceed lower price';
              }

              if (!current) {
                return;
              }

              switch (name) {
                case 'minPriceInEth':
                  switch (poolType) {
                    case 'pair':
                      if (value > current) {
                        return 'Out of range';
                      }
                      break;
                    case 'eth':
                      if (value > current) {
                        return 'Out of range';
                      }
                      break;
                    case 'nft':
                      if (value < current) {
                        return 'Out of range';
                      }
                      break;
                    default:
                      break;
                  }
                  break;
                case 'maxPriceInEth':
                  switch (poolType) {
                    case 'pair':
                      if (value < current) {
                        return 'Out of range';
                      }
                      break;
                    case 'eth':
                      if (value > current) {
                        return 'Out of range';
                      }
                      break;
                    case 'nft':
                      if (value < current) {
                        return 'Out of range';
                      }
                      break;
                    default:
                      break;
                  }
                  break;
                default:
                  break;
              }
            },
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <fieldset>
                <div className="flex">
                  <button
                    type="button"
                    className="text-xl text-gray-500 leading-none pr-1"
                    disabled={fullRange || !canEditRange || !hasPrice}
                    onClick={() => {
                      if (tickKey === 'tickLower') {
                        const value = (tickLower || 0) - tickSpacing;
                        setValue('tickLower', value);
                        setDisabled(true);
                        onTickLowerChange(value);
                      } else {
                        const value = (tickUpper || 0) - tickSpacing;
                        setValue('tickUpper', value);
                        setDisabled(true);
                        onTickUpperChange(value);
                      }
                    }}
                  >
                    {'-'}
                  </button>
                  <span className="flex-1">
                    <input
                      {...field}
                      type="number"
                      min="0"
                      disabled={!canEditRange || !hasPrice}
                      className="p-1 text-center text-xl border-none bg-gray-50 focus:outline-0 focus:ring-0 min-w-0 w-full mx-2 max-w-[8ch]"
                      onChange={(e) => {
                        field.onChange(e);
                        setDisabled(true);
                        if (name === 'minPriceInEth') {
                          onMinPriceChange(e.target.value);
                        } else {
                          onMaxPriceChange(e.target.value);
                        }
                      }}
                    />
                  </span>
                  <button
                    type="button"
                    className="text-xl text-gray-500 leading-none pl-1"
                    disabled={fullRange || !canEditRange || !hasPrice}
                    onClick={() => {
                      setDisabled(true);
                      if (tickKey === 'tickLower') {
                        const value = (tickLower || 0) + tickSpacing;
                        setValue('tickLower', value);
                        onTickLowerChange(value);
                      } else {
                        const value = (tickUpper || 0) + tickSpacing;
                        setValue('tickUpper', value);
                        onTickUpperChange(value);
                      }
                    }}
                  >
                    {'+'}
                  </button>
                </div>
                {!!error && <div className="text-red-500">{error.message}</div>}
              </fieldset>
            );
          }}
        />
      )}

      <span className="text-xs text-gray-500">
        {t('range.ratio', { symbol })}
      </span>
      {showTicks && (
        <div className="text-xs text-gray-400">{`${
          tickKey === 'tickLower' ? 'lower' : 'upper'
        } tick: ${tickKey === 'tickLower' ? tickLower : tickUpper}`}</div>
      )}
    </div>
  );
};

export default PriceInEth;
